import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/firebase';
import { setBrowserChrome } from '../hooks/usePageChrome';

// The two values `--site-bg` takes in App.css. Duplicated here because the
// browser chrome is set from JavaScript and cannot read a custom property that
// is declared on the same element it is about to paint behind — keep the pair
// in step with the `:root` / `[data-theme="dark"]` blocks there.
const SITE_BG_LIGHT = '#F3F4F8';
const SITE_BG_DARK = '#12141c';

// ── WHERE THE CHOICE LIVES, AND WHY IT MOVED ─────────────────────────────────
// `tunify_theme` (the old key) cannot be trusted to mean anything. The effect
// below used to write it on every mount, including the very first one — so the
// default was persisted before anyone had touched a control, and every visitor
// who ever opened the site has "light" saved whether they wanted it or not.
// Reading that back is indistinguishable from an explicit preference, so
// flipping the default alone would have changed nothing for anybody.
//
// This key is written ONLY by setTheme/toggleTheme, i.e. only when a person
// actually picks. Absent means "never chose", which is what lets a default
// mean something. The old key is deliberately not migrated — its value is not
// evidence of a preference. Anyone who had genuinely chosen dark lands on dark
// anyway, since that is now the default; the only people who have to re-pick
// are those who explicitly chose light.
const CHOICE_KEY = 'tunify_theme_choice';
const DEFAULT_THEME = 'dark';

// ── THE CHOICE IS AN ACCOUNT PREFERENCE ──────────────────────────────────────
// The only control that writes it is the item in UserMenu, which renders for a
// signed-in user and nobody else (ThemeToggle exists but is mounted nowhere).
// So a signed-OUT visitor cannot express a preference here — and a value left
// in storage by whoever last used the browser is not one of theirs.
//
// That is not hypothetical: pick light while signed in, sign out, and the
// marketing pages stay dark while /shop, /search and a product page turn white,
// which is the split-personality the dark default was introduced to end. Signed
// out, the site looks like its front door. The choice is KEPT, not cleared, so
// signing back in restores it.
const SIGNED_IN_KEY = 'tunify_signed_in';

/**
 * What actually gets painted.
 *
 * Pure and exported so the rule can be tested without a browser or a Firebase
 * session — the two things that make this awkward to exercise otherwise.
 */
export function resolveTheme(choice, signedIn) {
  if (!signedIn) return DEFAULT_THEME;
  return choice === 'light' || choice === 'dark' ? choice : DEFAULT_THEME;
}

function readChoice() {
  try {
    const chosen = localStorage.getItem(CHOICE_KEY);
    if (chosen === 'dark' || chosen === 'light') return chosen;
  } catch { /* private mode — fall through to the default */ }
  return null;
}

// A SYNCHRONOUS ECHO of "is anyone signed in", for the pre-paint script in
// index.html. Firebase keeps the session in IndexedDB by default, which cannot
// be read before the first paint, so the real answer is not available in time.
// This is only ever a hint about the LAST load: onAuthStateChanged corrects it
// a moment later, and the cost of it being stale is one frame of the other
// theme rather than a wrong page.
function lastKnownSignedIn() {
  try { return localStorage.getItem(SIGNED_IN_KEY) === '1'; } catch { return false; }
}

const ThemeContext = createContext(null);

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}

// Covers the marketing/account pages (Landing, Login/Register/Forgot Password,
// Dashboard, Settings) — these already look light today (white cards, dark
// text); this adds dark as the alternative. Deliberately scoped OUT of the 3D
// Room Editor / Builder / FloorPlan / House3DView, which is its own
// heavily-tuned dark UI overlaying a 3D canvas (its own colour system, in
// App.css's :root, is untouched by this).
export function ThemeProvider({ children }) {
  // DARK BY DEFAULT. The landing page and the sign-in screens both fix their
  // own dark palette, so a visitor who came through either of them and then
  // opened /designers or /shop was thrown from a dark page onto a white one
  // mid-journey. Dark is what the product looks like; light is now the opt-in.
  const [choice, setChoice] = useState(readChoice);

  // SUBSCRIBED DIRECTLY, not through useAuth: this provider wraps AuthProvider
  // (see App.jsx) and so cannot consume its context. Firebase allows any number
  // of listeners, and taking one here keeps the whole rule in this file rather
  // than making the auth layer responsible for theming.
  const [signedIn, setSignedIn] = useState(lastKnownSignedIn);
  useEffect(() => onAuthStateChanged(auth, (current) => {
    const now = !!current;
    setSignedIn(now);
    // Written on every transition, so the next cold load paints the right
    // theme before React exists. See lastKnownSignedIn.
    try { localStorage.setItem(SIGNED_IN_KEY, now ? '1' : '0'); } catch { /* private mode */ }
  }), []);

  const themeState = resolveTheme(choice, signedIn);

  // PAINTS ONLY. Persisting here is what broke the default in the first place:
  // an effect runs on mount, so it saved a preference nobody had expressed.
  // Storage is written by the setters below and nowhere else.
  //
  // index.html sets this same attribute inline before the first paint, so this
  // is normally re-applying a value that is already correct — it exists for the
  // toggle, not for the initial render.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeState);
    // KEEP THE BROWSER CHROME MATCHING THE PAGE. iOS extends the document under
    // the status bar and the Safari toolbar and tints both from the canvas, so
    // a canvas that does not match the page shows as a pale band top and
    // bottom. These are the same two values `--site-bg` takes in App.css; a
    // page that fixes its own palette overrides this (see usePageChrome).
    setBrowserChrome(themeState === 'dark' ? SITE_BG_DARK : SITE_BG_LIGHT);
  }, [themeState]);

  const persist = useCallback((next) => {
    setChoice(next);
    try { localStorage.setItem(CHOICE_KEY, next); } catch { /* private mode — just won't stick */ }
  }, []);

  const setTheme = useCallback((next) => {
    if (next === 'dark' || next === 'light') persist(next);
  }, [persist]);

  const toggleTheme = useCallback(() => {
    persist(themeState === 'dark' ? 'light' : 'dark');
  }, [persist, themeState]);

  return (
    <ThemeContext.Provider value={{ theme: themeState, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
